import { captureError } from '#shared/lib/logger'

type ErrorContext = Record<string, unknown>

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return isChunkLoadErrorMessage(message)
}

/**
 * Captures errors and sends the errors to services.
 *
 * In dev mode, errors are sent to the console.
 * In production mode, client-side errors are handled by the tracking service.
 *
 * Server-side errors are logged to the console and picked up by Datadog.
 */
export default defineNuxtPlugin((nuxtApp) => {
  const headers = useRequestHeaders()

  // useRequestHeaders lowercases keys on the server. Keep the Pascal-case
  // fallback for mocks and any non-standard header maps.
  const referrer = isMetaClient
    ? document.referrer
    : (headers.referer ?? headers.Referer)

  const $captureError = (error: unknown, context?: ErrorContext) => {
    const errorContext: ErrorContext = { ...context }

    // If the context contains no internal referrer, use the referrer from the
    // request headers (or document.referrer on the client).
    if (!errorContext.referrer) {
      errorContext.referrer = referrer
    }

    if (isMetaDev) {
      console.error(error, errorContext)
    }
    else {
      if (isMetaClient) {
        trackError(error)
      }
      captureError(error, errorContext)
    }
  }

  // Capture Vue client-side errors.
  nuxtApp.hook('vue:error', (error, instance, info) => {
    // Chunk errors should be swallowed to not polute DataDog.
    if (isChunkLoadError(error)) {
      return
    }

    // Only the component name is captured. Serializing the instance itself
    // pulls in the app context and the whole parent chain.
    const { __name: component } = (instance?.$options ?? {}) as { __name?: string }

    $captureError(error, { component, info })
  })

  // Capture Nuxt server-side errors.
  nuxtApp.hook('app:error', (error) => {
    $captureError(error)
  })

  return {
    provide: {
      captureError: $captureError,
    },
  }
})
